import {
    Box, Typography, Table, TableHead, TableRow, TableCell, TableBody, TextField
} from "@mui/material";
import { formatarInputValorMonetárioSemPrefixo, formatarMonetárioParaNúmero, formatarValorMonetario } from "../../../../../../../../helpers/masks";
import { ProdutoGerarOc } from "..";

interface Step02Props {
    produtos: ProdutoGerarOc[];
    valorFrete: number;
    descontoFrete: string;
    totalComFrete: number;
    onChangeDescontoFrete: (valor: string) => void;
    onChangeDescontoUnitario: (produto_id: number, valor: string) => void;
    onChangeAcrescimoUnitario: (produto_id: number, valor: string) => void;
    fornecedor: string;
}

export default function Step02({
    produtos,
    valorFrete,
    descontoFrete,
    totalComFrete,
    onChangeDescontoFrete,
    onChangeDescontoUnitario,
    onChangeAcrescimoUnitario,
    fornecedor
}: Step02Props) {

    const descontoMaiorQueFrete = formatarMonetárioParaNúmero(descontoFrete) > Number(valorFrete);

    return (
        <Box>
            <Typography variant="h5" fontWeight={600} mb={1} sx={{ fontSize: { xs: '1rem', md: '1.12rem' } }}>
                Configuração da Ordem de Compra - {fornecedor}
            </Typography>
            <Typography fontSize={13} color="text.secondary" mb={2} sx={{ fontSize: { xs: '0.87rem', md: '0.92rem' } }}>
                Defina os descontos e acréscimos para os produtos deste fornecedor.
            </Typography>
            <Table sx={{ mb: { xs: 1.5, md: 2 }, fontSize: { xs: '0.85rem', md: '0.92rem' } }}>
                <TableHead>
                    <TableRow>
                        <TableCell><b>Produto</b></TableCell>
                        <TableCell align="right"><b>Qtd. solicitada</b></TableCell>
                        <TableCell align="right"><b>Valor unitário</b></TableCell>
                        <TableCell align="right"><b>Desconto Unit.</b></TableCell>
                        <TableCell align="right"><b>Acréscimo Unit.</b></TableCell>
                        <TableCell align="right"><b>Valor total</b></TableCell>
                        <TableCell align="right"><b>Prev. entrega</b></TableCell>
                    </TableRow>
                </TableHead>
                <TableBody>
                    {produtos.map((produto) => {
                        return (
                            <TableRow key={produto.id} hover sx={{ fontSize: { xs: '0.85rem', md: '0.92rem' } }}>
                                <TableCell>
                                    <Typography variant="subtitle2">{produto.produto_id} {produto.nome}</Typography>
                                    {produto.observacoes && (
                                        <Typography
                                            variant="subtitle2"
                                            color="text.secondary" sx={{ fontSize: { xs: '0.82rem', md: '0.9rem' } }}>
                                            {produto.observacoes}
                                        </Typography>
                                    )}
                                </TableCell>
                                <TableCell align="right">
                                    {produto.quantidade} {produto.unidade_medida}
                                </TableCell>
                                <TableCell align="right">
                                    {formatarValorMonetario(produto.valor_unitario)}
                                </TableCell>
                                <TableCell align="right">
                                    <TextField
                                        size="small"
                                        type="text"
                                        value={produto.valor_desconto}
                                        onChange={(e) => {
                                            const novoValor = formatarInputValorMonetárioSemPrefixo(e.target.value);
                                            onChangeDescontoUnitario(produto.produto_id, novoValor);
                                        }}
                                        sx={{ width: 110 }}
                                    />
                                </TableCell>
                                <TableCell align="right">
                                    <TextField
                                        size="small"
                                        type="text"
                                        value={produto.valor_acrescimo}
                                        onChange={(e) => {
                                            const novoValor = formatarInputValorMonetárioSemPrefixo(e.target.value);
                                            onChangeAcrescimoUnitario(produto.produto_id, novoValor);
                                        }}
                                        sx={{ width: 110 }}
                                    />
                                </TableCell>
                                <TableCell align="right" sx={{ color: getValorTotalColor(produto.valor_desconto?.toString() || '0', produto.valor_acrescimo?.toString() || '0') }}>
                                    {formatarValorMonetario((produto.valor_unitario - formatarMonetárioParaNúmero(produto.valor_desconto || '0') + formatarMonetárioParaNúmero(produto.valor_acrescimo || '0')) * produto.quantidade)}
                                </TableCell>
                                <TableCell align="right">
                                    {produto.prazo_entrega || "-"}
                                </TableCell>
                            </TableRow>
                        );
                    })}
                </TableBody>
            </Table>
            <Box display="flex" flexDirection="column" alignItems="flex-end" mt={2}>
                <Box display="flex" alignItems="center" gap={2} mb={1}>
                    <Typography sx={{ fontSize: { xs: '0.86rem', md: '0.93rem' } }}>Frete</Typography>
                    <Typography fontWeight={700} sx={{ fontSize: { xs: '0.93rem', md: '1rem' } }}>R$ {Number(valorFrete).toFixed(2)}</Typography>
                </Box>
                <Box display="flex" alignItems="center" gap={2} mb={descontoMaiorQueFrete ? 0 : 1}>
                    <Typography sx={{ fontSize: { xs: '0.86rem', md: '0.93rem' } }}>Desconto de frete</Typography>
                    <TextField
                        size="small"
                        type="text"
                        value={descontoFrete}
                        onChange={e => {
                            const novoValor = formatarInputValorMonetárioSemPrefixo(e.target.value)
                            onChangeDescontoFrete(novoValor)
                        }}
                        sx={{ width: 110, fontSize: { xs: '0.86rem', md: '0.93rem' } }}
                        slotProps={{ htmlInput: { min: 0, step: 0.01, max: Number(valorFrete) } }}
                        error={descontoMaiorQueFrete}
                    />
                </Box>
                {descontoMaiorQueFrete && (
                    <Typography color="error" my={1} sx={{ fontSize: { xs: '0.75rem', md: '0.8rem' } }}>
                        O desconto não pode ser maior que o valor do frete.
                    </Typography>
                )}
                <Box display="flex" alignItems="center" gap={2} mb={2}>
                    <Typography fontWeight={700} sx={{ fontSize: { xs: '0.93rem', md: '1rem' } }}>TOTAL + FRETE</Typography>
                    <TextField
                        size="small"
                        value={formatarValorMonetario(totalComFrete)}
                        sx={{ width: 130, fontWeight: 700, fontSize: { xs: '0.93rem', md: '1rem' } }}
                        slotProps={{ input: { readOnly: true } }}
                    />
                </Box>
            </Box>
        </Box>
    );
}

function getValorTotalColor(descontos: string, acrescimos: string) {
    const desconto = formatarMonetárioParaNúmero(descontos);
    const acrescimo = formatarMonetárioParaNúmero(acrescimos);

    if (acrescimo > 0 && acrescimo > desconto) {
        return 'red';
    }
    if (desconto > 0 && desconto > acrescimo) {
        return 'green';
    }
    return 'inherit';
}
